import { SportType } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';
import { keyLookup } from '@/libraries/utils/utils';

export interface ISportType {
    sportType: SportType;
    text: string;
}

export function isSoccer(sportType: SportType) {
    return sportType === SportType.Soccer;
}

export function getSportTypeKey(sportType: number) {
    return keyLookup(SportType, sportType);
}

export function noNeedCheckMatchCount(sportType: SportType) {
    return [
        SportType.ESportsIm,
        SportType.VirtualFootball,
        SportType.VirtualBasketball,
        SportType.FantasySelection,
        SportType.MuayThaiOneBx,
    ].includes(sportType);
}

export function isESports(sportType: SportType) {
    return sportType === SportType.ESportsForDisplay;
}

export function isVirtualSport(sportType: SportType) {
    return [SportType.VirtualBasketball, SportType.VirtualFootball].includes(sportType);
}

export function isImEsports(sportType: SportType) {
    return sportType === SportType.ESportsIm;
}

export function isExchange(sportType: SportType) {
    return sportType === SportType.Exchange;
}

export function isFantasySelection(sportType: SportType) {
    return sportType === SportType.FantasySelection;
}

export function isMuayThaiForDisplay(sportType: SportType) {
    return sportType === SportType.MuayThaiForDisplay;
}

export function isMuayThaiOneBx(sportType: SportType) {
    return sportType === SportType.MuayThaiOneBx;
}

export function getMuayThaiSubLabelKey(sportType: SportType): string {
    if (sportType === SportType.MuayThai) return 'sportType_MuayThaiSbo';
    return `sportType_${getSportTypeKey(sportType)}`;
}

export function getSportsList(): ISportType[] {
    const excludeSportType = [
        SportType.Unknown,
        SportType.ESportsForDisplay,
        SportType.ESportsIm,
        SportType.VirtualFootball,
        SportType.VirtualBasketball,
        SportType.VirtualRacing,
        SportType.Racing,
        SportType.Exchange,
        SportType.Financial,
        SportType.FantasySelection,
        SportType.MuayThaiForDisplay,
        SportType.MuayThaiOneBx,
        SportType.VirtualNBA,
    ];

    const sportTypeList = Object.values(SportType)
        .filter(sportType => typeof sportType === 'number')
        .map(sportType => sportType as SportType);
    const sportTypes = sportTypeList.filter(sportType => !excludeSportType.includes(sportType));
    return sportTypes
        .map(sportType => ({
            sportType,
            text: getSportText(sportType),
        }));
}

function getSportText(sportType: SportType) {
    return i18n.t(`sportType_${getSportTypeKey(sportType)}`);
}

const defaultSportList: SportType[] = [
    SportType.Soccer,
    SportType.Basketball,
    SportType.Badminton,
    SportType.Tennis,
    SportType.Volleyball,
    SportType.ESports,
    SportType.TableTennis,
    SportType.Football,
    SportType.Baseball,
    SportType.Pool,
    SportType.IceHockey,
    SportType.Golf,
    SportType.Olympic,
    SportType.MuayThai,
    SportType.Rugby,
    SportType.MixedMartialArts,
    SportType.Handball,
    SportType.MotorSport,
    SportType.Futsal,
    SportType.Cricket,
    SportType.Darts,
    SportType.Boxing,
    SportType.BeachVolleyball,
    SportType.AussieRulesFootball,
    SportType.GeneralHockey,
    SportType.WaterPolo,
    SportType.WinterSports,
    SportType.Bandy,
    SportType.Cycling,
    SportType.Athletics,
    SportType.Squash,
    SportType.WinterOlympic,
    SportType.BeachSoccer,
    SportType.Others,
    SportType.Entertainment,
];

const sportListByCurrency: Partial<Record<string, SportType[]>> = {
    Pp: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Tennis,
        SportType.Volleyball,
        SportType.ESports,
        SportType.TableTennis,
        SportType.Football,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.MixedMartialArts,
        SportType.MotorSport,
        SportType.Golf,
        SportType.Olympic,
        SportType.Pool,
        SportType.Rugby,
        SportType.MuayThai,
        SportType.Futsal,
        SportType.Handball,
        SportType.AussieRulesFootball,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.Cricket,
        SportType.Darts,
        SportType.BeachSoccer,
        SportType.WaterPolo,
        SportType.Bandy,
        SportType.GeneralHockey,
        SportType.Cycling,
        SportType.Athletics,
        SportType.WinterSports,
        SportType.Squash,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    TB: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Tennis,
        SportType.Volleyball,
        SportType.MuayThai,
        SportType.TableTennis,
        SportType.ESports,
        SportType.Pool,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.Football,
        SportType.Cricket,
        SportType.Rugby,
        SportType.Golf,
        SportType.Olympic,
        SportType.MixedMartialArts,
        SportType.Handball,
        SportType.MotorSport,
        SportType.Futsal,
        SportType.BeachVolleyball,
        SportType.Boxing,
        SportType.Darts,
        SportType.AussieRulesFootball,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.Bandy,
        SportType.Cycling,
        SportType.WinterSports,
        SportType.Athletics,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    VND: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Tennis,
        SportType.Badminton,
        SportType.ESports,
        SportType.Volleyball,
        SportType.TableTennis,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.Football,
        SportType.Futsal,
        SportType.Pool,
        SportType.MotorSport,
        SportType.Golf,
        SportType.Olympic,
        SportType.Handball,
        SportType.Cricket,
        SportType.MuayThai,
        SportType.WaterPolo,
        SportType.Athletics,
        SportType.GeneralHockey,
        SportType.Cycling,
        SportType.Boxing,
        SportType.MixedMartialArts,
        SportType.Bandy,
        SportType.Darts,
        SportType.BeachVolleyball,
        SportType.BeachSoccer,
        SportType.AussieRulesFootball,
        SportType.Rugby,
        SportType.WinterSports,
        SportType.Squash,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    KRW: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Tennis,
        SportType.Baseball,
        SportType.Volleyball,
        SportType.ESports,
        SportType.Badminton,
        SportType.IceHockey,
        SportType.Handball,
        SportType.Pool,
        SportType.TableTennis,
        SportType.Football,
        SportType.MixedMartialArts,
        SportType.Rugby,
        SportType.MuayThai,
        SportType.Futsal,
        SportType.Cricket,
        SportType.Golf,
        SportType.Olympic,
        SportType.MotorSport,
        SportType.Darts,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.AussieRulesFootball,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.WinterSports,
        SportType.Bandy,
        SportType.Cycling,
        SportType.Athletics,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    INR: [
        SportType.Cricket,
        SportType.Soccer,
        SportType.Tennis,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Volleyball,
        SportType.ESports,
        SportType.TableTennis,
        SportType.Football,
        SportType.Baseball,
        SportType.MuayThai,
        SportType.Golf,
        SportType.Olympic,
        SportType.Pool,
        SportType.IceHockey,
        SportType.Rugby,
        SportType.MixedMartialArts,
        SportType.Handball,
        SportType.MotorSport,
        SportType.Darts,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.AussieRulesFootball,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.Futsal,
        SportType.WinterSports,
        SportType.Bandy,
        SportType.Cycling,
        SportType.Athletics,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
};

const sportListByCountry: Partial<Record<string, SportType[]>> = {
    ID: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Tennis,
        SportType.Volleyball,
        SportType.ESports,
        SportType.TableTennis,
        SportType.Football,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.MixedMartialArts,
        SportType.MotorSport,
        SportType.Golf,
        SportType.Olympic,
        SportType.Pool,
        SportType.Rugby,
        SportType.MuayThai,
        SportType.Futsal,
        SportType.Handball,
        SportType.AussieRulesFootball,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.Cricket,
        SportType.Darts,
        SportType.BeachSoccer,
        SportType.WaterPolo,
        SportType.Bandy,
        SportType.GeneralHockey,
        SportType.Cycling,
        SportType.Athletics,
        SportType.WinterSports,
        SportType.Squash,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    TH: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Tennis,
        SportType.Volleyball,
        SportType.MuayThai,
        SportType.TableTennis,
        SportType.ESports,
        SportType.Pool,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.Football,
        SportType.Cricket,
        SportType.Rugby,
        SportType.Golf,
        SportType.Olympic,
        SportType.MixedMartialArts,
        SportType.Handball,
        SportType.MotorSport,
        SportType.Futsal,
        SportType.BeachVolleyball,
        SportType.Boxing,
        SportType.Darts,
        SportType.AussieRulesFootball,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.Bandy,
        SportType.Cycling,
        SportType.WinterSports,
        SportType.Athletics,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    VN: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Tennis,
        SportType.Badminton,
        SportType.ESports,
        SportType.Volleyball,
        SportType.TableTennis,
        SportType.Baseball,
        SportType.IceHockey,
        SportType.Football,
        SportType.Futsal,
        SportType.Pool,
        SportType.MotorSport,
        SportType.Golf,
        SportType.Olympic,
        SportType.Handball,
        SportType.Cricket,
        SportType.MuayThai,
        SportType.WaterPolo,
        SportType.Athletics,
        SportType.GeneralHockey,
        SportType.Cycling,
        SportType.Boxing,
        SportType.MixedMartialArts,
        SportType.Bandy,
        SportType.Darts,
        SportType.BeachVolleyball,
        SportType.BeachSoccer,
        SportType.AussieRulesFootball,
        SportType.Rugby,
        SportType.WinterSports,
        SportType.Squash,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    KR: [
        SportType.Soccer,
        SportType.Basketball,
        SportType.Tennis,
        SportType.Baseball,
        SportType.Volleyball,
        SportType.ESports,
        SportType.Badminton,
        SportType.IceHockey,
        SportType.Handball,
        SportType.Pool,
        SportType.TableTennis,
        SportType.Football,
        SportType.MixedMartialArts,
        SportType.Rugby,
        SportType.MuayThai,
        SportType.Futsal,
        SportType.Cricket,
        SportType.Golf,
        SportType.Olympic,
        SportType.MotorSport,
        SportType.Darts,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.AussieRulesFootball,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.WinterSports,
        SportType.Bandy,
        SportType.Cycling,
        SportType.Athletics,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
    IN: [
        SportType.Cricket,
        SportType.Soccer,
        SportType.Tennis,
        SportType.Basketball,
        SportType.Badminton,
        SportType.Volleyball,
        SportType.ESports,
        SportType.TableTennis,
        SportType.Football,
        SportType.Baseball,
        SportType.MuayThai,
        SportType.Golf,
        SportType.Olympic,
        SportType.Pool,
        SportType.IceHockey,
        SportType.Rugby,
        SportType.MixedMartialArts,
        SportType.Handball,
        SportType.MotorSport,
        SportType.Darts,
        SportType.Boxing,
        SportType.BeachVolleyball,
        SportType.AussieRulesFootball,
        SportType.GeneralHockey,
        SportType.WaterPolo,
        SportType.Futsal,
        SportType.WinterSports,
        SportType.Bandy,
        SportType.Cycling,
        SportType.Athletics,
        SportType.Squash,
        SportType.BeachSoccer,
        SportType.WinterOlympic,
        SportType.Others,
        SportType.Entertainment,
    ],
};

export function getSportsListByCurrency(currency: string): SportType[] {
    return sportListByCurrency[currency] || defaultSportList;
}

export function getSportsListByCountry(countryCode: string): SportType[] {
    return sportListByCountry[countryCode] || defaultSportList;
}

const defaultSimulationSportList: SportType[] = [
    SportType.Soccer,
    SportType.Basketball,
    SportType.Tennis,
    SportType.IceHockey,
    SportType.Cricket,
];

const simulationSportsListByCurrency: Partial<Record<string, SportType[]>> = {
    INR: [
        SportType.Cricket,
        SportType.Soccer,
        SportType.Tennis,
        SportType.Basketball,
        SportType.IceHockey,
    ],
};

function getSimulationSportListByCurrency(currency: string): SportType[] {
    return simulationSportsListByCurrency[currency] ?? defaultSimulationSportList;
}

export function getOrderedSimulationSportList(
    leagueIds: Partial<Record<SportType, number[]>>,
    currency: string,
): SportType[] {
    const supportedSports = Object.keys(leagueIds).map(key => Number.parseInt(key)) as SportType[];
    return getSimulationSportListByCurrency(currency).filter(sport => supportedSports.includes(sport));
}
